import parse from 'html-react-parser';
import { isMobileOnly } from 'react-device-detect';
import { toast, ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import styles from './ToastNotification.module.scss';

const buildTemplate = (type, text) => {
  // For error messages, sanitize them for users while logging technical details
  if (type === 'error' && typeof text === 'string') {
    // Log the full technical error for developers
    // eslint-disable-next-line no-console
    console.error('Toast Error (Technical):', text);

    // Check if it's an API error with a specific format
    if (text.includes('API call to') && text.includes('failed with status')) {
      const parts = text.split(' failed with status ');
      if (parts.length === 2) {
        const errorDetails = parts[1];

        // Extract status code for user-friendly message
        const statusMatch = errorDetails.match(/(\d+):/);
        const statusCode = statusMatch ? statusMatch[1] : 'Unknown';

        // Create user-friendly message based on status code
        let userMessage = 'Something went wrong while processing your request.';
        if (statusCode === '404') {
          userMessage = 'The requested data could not be found. Please try again.';
        } else if (statusCode === '500') {
          userMessage = 'There was a server error. Please try again later.';
        } else if (statusCode === '401' || statusCode === '403') {
          userMessage = 'You do not have permission to access this data.';
        } else if (statusCode === '400') {
          userMessage = 'There was an issue with your request. Please check your settings.';
        }

        return (
          <div className={`${styles.toast} ${styles[type]}`}>
            <div className={styles['error-content']}>
              <div className={styles['error-title']}>Export Failed</div>
              <div className={styles['error-message']}>
                {userMessage}
                <br />
                <small>Error code: {statusCode}</small>
              </div>
            </div>
          </div>
        );
      }
    }

    // For other error messages, sanitize them
    if (text.includes('Failed to') || text.includes('Export failed')) {
      const title = text.includes('Export failed') ? 'Export Failed' : 'Operation Failed';

      // Create user-friendly message
      let userMessage = text;
      if (text.includes('Failed to generate PowerPoint')) {
        userMessage = 'Unable to create the PowerPoint file. Please try again.';
      } else if (text.includes('Failed to start export')) {
        userMessage = 'Unable to start the export process. Please try again.';
      } else if (text.includes('Export failed:')) {
        userMessage = 'The export process encountered an error. Please try again.';
      }

      return (
        <div className={`${styles.toast} ${styles[type]}`}>
          <div className={styles['error-content']}>
            <div className={styles['error-title']}>{title}</div>
            <div className={styles['error-message']}>{userMessage}</div>
          </div>
        </div>
      );
    }

    // For any other error messages, show a generic user-friendly message
    return (
      <div className={`${styles.toast} ${styles[type]}`}>
        <div className={styles['error-content']}>
          <div className={styles['error-title']}>Something went wrong</div>
          <div className={styles['error-message']}>
            An unexpected error occurred. Please try again or contact support if the problem
            persists.
          </div>
        </div>
      </div>
    );
  }

  // Default template for non-error or simple messages
  return <div className={`${styles.toast} ${styles[type]}`}>{parse(text)}</div>;
};

const toastNotify = (type = 'success', text) => toast(buildTemplate(type, text));

const ToastNotification = () => (
  <ToastContainer
    position="top-center"
    autoClose={isMobileOnly ? 3000 : 6000}
    hideProgressBar
    pauseOnFocusLoss={false}
    closeButton={!isMobileOnly}
    limit={4}
    style={{
      width: 'auto',
      maxWidth: '90vw',
      minWidth: '300px',
    }}
    toastStyle={{
      width: 'auto',
      maxWidth: '90vw',
      minWidth: '300px',
    }}
  />
);

export { ToastNotification, toastNotify };
